import { AvailabilityEnum, AvailabilityExceptionType, Document, Fleet, FuelType, Location, LatLng, OpeningHours, SailingSchedule, TankCladdingType, TankConfiguration, TankNumberingScheme, TankPosition, TransportObjectType, VesselType } from "__generated__/graphql";
import { Contact } from "types/commons";
import { Call, Product, Relation } from "types/index";
import { BargeType, CharterSubtype } from "__generated__/enums";

export type Ship = {
	id: number;
	name: string;

	externalId?: string;
	abbreviation?: string;
	eni?: string;
	mmsi?: number;
	mmsiUpdated?: number;
	imo?: number;
	trackerId?: number;
	deprecated?: boolean
	trackTraceProvider?: string;
	licensePlate?: string;

	vesselType?: VesselType;
	bargeType?: BargeType | null;
	yearOfBuild?: number;
	yearLastMaintenance?: number;

	position?: ShipPosition;
	nextCall?: Call;
	documents: Document[];

	//Measurements
	lengthCm?: number;
	widthCm?: number;
	capacityTon?: number;
	capacityM3?: number;
	capacityTeu?: number;
	maxDraughtCm?: number;
	minDraughtCm?: number;
	fuelType?: FuelType;
	avgVelocityEmptyKmH?: number;
	avgVelocityFullKmH?: number;
	avgFuelConsumptionLtrH?: number;
	avgFuelConsumptionLtrKm?: number;
	avgSpeedUpstreamKmH?: number;
	avgSpeedDownstreamKmH?: number;
	bobbyCat?: boolean;
	tracks?: boolean;
	hasPushBow?: boolean;
	hasPushingCertificate?: boolean;
	sailors?: boolean;
	memo?: string;
	numReeferConnections?: boolean;
	dropline?: boolean;
	deadWeight?: boolean;
	layerRemark?: string;
	gmp?: string;
	niwo?: string;
	trussing?: string;
	cellGuides?: boolean;
	hasShutters?: boolean;
	shuttersType?: string;
	deckLoad?: number;
	flag?: string;
	numberOfCranes?: number;
	maxCranesOnQuay?: number;
	maxTonBallast?: number;
	airDraft?: number;
	corridor?: string;
	tankEye?: boolean;
	hasHeatingInstallation?: boolean;

	holds: Hold[];
	tanks: Tank[];
	tankProperties?: TankProperties;
	bayPlan?: string;

	type?: TransportObjectType
	tonnageCertificates?: TonnageCertificate[]

	//Availability
	availability?: ShipAvailability;
	availabilityExceptions: ShipAvailabilityException[];
	workingHours: OpeningHours

	//Relations
	shipManager?: Relation | null;
	skipper?: Relation | null;
	owner?: Relation | null;
	operator?: Relation | null;
	insurer?: string;
	fleets?: Fleet[];
	agents?: ShipLocationAgent[];
	coupledShip?: {
		id: number;
		name: string;
	};
	isCoupled?: boolean;

	//Contacts
	invoiceContact?: Contact;
	contacts?: Contact[];

	lastShippedProducts?: Product[] | null;
	showLastProducts?: boolean;

	retrieveActuals?: boolean;
	tracking?: boolean;
	isDummy?: boolean;
	updatedOn?: number;
	createdOn?: number;
	entityVersion?: number;

	advancedTracking?: boolean;
	available?: boolean;

	classificationSociety?: string;
	adnProperties?: ShipAdnProperties;
	boiler?: boolean;
	spillRail?: boolean;
	tankCladdingType: TankCladdingType;
	pumpCapacity?: number;
	numberOfTanks?: number;

	sailingSchedule?: SailingSchedule;

	defaultCharterSubtype?: CharterSubtype | null;
}

export type ShipPosition = {
	id?: number;
	latitude?: number;
	longitude?: number;
	course?: number;
	speed?: number;
	timestamp?: number;
	source?: string;
	location: string
	locationEntered?: number;
	ship: {
		id: number;
		name: string;
	}
	communicatedToCustomer?: number;
}

export type Hold = {
	id: number;
	capacityTon?: number;
	capacityM3?: number;
	lengthCm?: number;
	widthCm?: number;
	depthCm?: number;
	ship: {
		id: number;
		name: string;
	}
}

export type ShipLocationAgent = {
	agent?: Relation;
	location?: Pick<Location, "id" | "description">;
	ship: {
		id: number;
		name: string;
	}
};

export type Tank = {
	id: number;
	capacityM3?: number | null;
	isAssignable?: boolean;
	position: TankPosition;
	positionNumber: number;
};

export type TankProperties = {
	tankConfiguration?: TankConfiguration;
	tankNumberingScheme?: TankNumberingScheme;
}

export type TonnageCertificate = {
	depth: number;
	tonnage?: number;
}

export type ShipBayPlanTemplate = {
	id: number;
	name: string;
	shipBargeType: BargeType;
	tiers: number;
	rows: number;
	bays: number;
	bayPlan: string;
	createdOn: number;
};

export type ShipAvailability = {
	availableMonday?: boolean;
	availableTuesday?: boolean;
	availableWednesday?: boolean;
	availableThursday?: boolean;
	availableFriday?: boolean;
	availableSaturday?: boolean;
	availableSunday?: boolean;
	defaultAvailability?: boolean;
	availableDayNight?: AvailabilityEnum;
	availableHoursADay?: number;
}

export type ShipAvailabilityException = {
	id?: number;
	ship?: {
		id: number;
		name: string;
	};
	startDate?: number;
	endDate?: number;
	remark?: string;
	startLocation?: string;
	startPosition?: LatLng;
	endLocation?: string;
	endPosition?: LatLng;
	startMasterdataLocation?: Pick<Location, "id" | "description">;
	endMasterdataLocation?: Pick<Location, "id" | "description">;
	type?: AvailabilityExceptionType;
	accepted?: number;
}

export type ShipAdnProperties = {
	shipType?: string;
	cargoTankDesign?: string;
	cargoTankType?: string;
}

export const AdnCargoTankDesign = {
	"1": "Pressure cargo tank",
	"2": "Closed cargo tank",
	"3": "Open cargo tank with flame arrester",
	"4": "Open cargo tank",
} as const;

export const AdnCargoTankType = {
	"1": "Independent cargo tank",
	"2": "Integral cargo tank",
	"3": "Cargo tank with walls distinct from the outer hull",
	"4": "Membrane tank",
} as const;

export const AdnShipType = {
	"G": "Gas",
	"C": "Chemical",
	"N": "Normal",
} as const;
